import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { API_URL, config, UserCode } from '../../../components/Include'
import { useSelector } from 'react-redux'
import { Alerts } from '../../../components/notify'
import './complaints.css'

const Complaints = () => {
  // State management
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [tickets, setTickets] = useState<any[]>([])
  const [activeTicket, setActiveTicket] = useState<any>(null)
  const [replyContent, setReplyContent] = useState('')
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    message: ''
  })
  const [showNewTicketForm, setShowNewTicketForm] = useState(false)
  const [filter, setFilter] = useState('all')
  
  // Get student data from Redux store
  const student = useSelector((state: any) => state.rootReducer.student)

  useEffect(() => {
    fetchTickets()
  }, [filter])
  
  // Get ticket details
  const getTicketDetails = async (ticketId: string) => {
    try {
      const response = await axios.post(
        `${API_URL}/student/complaints/details`,
        {
          ticket_id: ticketId,
          student_code: UserCode
        },
        config
      )
      if (response.data.status === 200) {
        setActiveTicket(response.data.data)
      } else {
        Alerts('warning', response.data.message, 'Warning')
      }
    } catch (error: any) {
      Alerts('danger', error.response?.data?.message || error.message, 'Error fetching ticket details')
    }
  }
  
  // Close ticket
  const closeTicket = async (ticketId: string) => {
   
    
    try {
      const response = await axios.post(
        `${API_URL}/student/complaints/close`,
        {
          ticket_id: ticketId,
          student_code: UserCode
        },
        config
      )

      if (response.data.status === 200) {
        // Update ticket status in the list
        setTickets(prevTickets => 
          prevTickets.map(ticket => 
            ticket.id === ticketId ? { ...ticket, status: 'closed' } : ticket
          )
        )
        
        // Update active ticket if it's the one being closed
        if (activeTicket && activeTicket.id === ticketId) {
          setActiveTicket({ ...activeTicket, status: 'closed' })
        }
        
        Alerts('success', 'Ticket closed successfully', 'Success')
      } else {
        Alerts('warning', response.data.message, 'Warning')
      }
    } catch (error: any) {
      Alerts('danger', error.response?.data?.message || error.message, 'Error closing ticket')
    }
  }

  // Fetch all tickets
  const fetchTickets = async () => {
    setLoading(true)
    try {
      const response = await axios.post(
        `${API_URL}/student/complaints/tickets`,
        {
          student_code: UserCode,
          status: filter
        },
        config
      )

      if (response.data.status === 200) {
        setTickets(response.data.data)
      } else {
        Alerts('warning', response.data.message, 'Warning')
        setTickets([])
      }
    } catch (error: any) {
      Alerts('danger', error.response?.data?.message || error.message, 'Error fetching tickets')
      setTickets([])
    } finally {
      setLoading(false)
    }
  }

  // Submit new ticket
  const handleSubmitTicket = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    
    try {
      // Validate form
      if (!newTicket.subject || !newTicket.category || !newTicket.message) {
        Alerts('warning', 'Please fill all required fields', 'Form Validation')
        setSubmitting(false)
        return
      }
      
      const response = await axios.post(
        `${API_URL}/student/complaints/create`,
        {
          student_code: UserCode,
          subject: newTicket.subject,
          category: newTicket.category,
          priority: newTicket.priority,
          sender_name: student.full_name,
          message: newTicket.message
        },
        config
      )

      if (response.data.status === 200) {
        // Reset form
        setNewTicket({
          subject: '',
          category: '',
          priority: 'medium',
          message: ''
        })
        
        // Close form
        setShowNewTicketForm(false)
        
        // Refresh tickets list
        fetchTickets()
        
        // Show success message
        Alerts('success', 'Your ticket has been submitted successfully', 'Success')
      } else {
        Alerts('warning', response.data.message, 'Warning')
      }
    } catch (error: any) {
      Alerts('danger', error.response?.data?.message || error.message, 'Error submitting ticket')
    } finally {
      setSubmitting(false)
    }
  }

  // Submit reply to ticket
  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    
    try {
      if (!activeTicket) return
      
      const response = await axios.post(
        `${API_URL}/student/complaints/reply`,
        {
          ticket_id: activeTicket.id,
          student_code: UserCode,
          message: replyContent
        },
        config
      )

      if (response.data.status === 200) {
        // Clear reply input
        setReplyContent('')
        
        // Refresh ticket details to show the new reply
        const detailsResponse = await axios.post(
          `${API_URL}/student/complaints/details`,
          {
            ticket_id: activeTicket.id,
            student_code: UserCode
          },
          config
        )
        
        if (detailsResponse.data.status === 200) {
          // Update active ticket with fresh data
          setActiveTicket(detailsResponse.data.data)
          
          // Update ticket in the list
          setTickets(prevTickets => 
            prevTickets.map(ticket => 
              ticket.id === activeTicket.id ? detailsResponse.data.data : ticket
            )
          )
        }
        
        // Show success message
        Alerts('success', 'Your reply has been sent successfully', 'Success')
      } else {
        Alerts('warning', response.data.message, 'Warning')
      }
    } catch (error: any) {
      Alerts('danger', error.response?.data?.message || error.message, 'Error sending reply')
    } finally {
      setSubmitting(false)
    }
  }

  // Format date to readable format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Get priority class for styling
  const getPriorityClass = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'priority-high'
      case 'medium':
        return 'priority-medium'
      case 'low':
        return 'priority-low'
      default:
        return 'priority-medium'
    }
  }

  // Get status class for styling
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'open':
        return 'status-open'
      case 'closed':
        return 'status-closed'
      case 'pending':
        return 'status-pending'
      default:
        return 'status-open'
    }
  }

  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'payment':
        return 'fa-money'
      case 'certificate':
        return 'fa-certificate'
      case 'course':
        return 'fa-book'
      case 'technical':
        return 'fa-wrench'
      case 'other':
        return 'fa-question-circle'
      default:
        return 'fa-ticket'
    }
  }

  return (
    <>

<div className="skills-header my-4">
          <h4><i className="fa fa-exchange-alt"></i> Support Tickets</h4>
          
          <button 
            className="btn btn-primary" 
            onClick={() => setShowNewTicketForm(!showNewTicketForm)}
          >
            <i className="fa fa-plus-circle"></i>
            {showNewTicketForm ? 'Cancel' : 'New Ticket'}
          </button>
        </div>



      <div className="row">
        {/* New Ticket Form */}
        {showNewTicketForm && (
          <div className="col-12 mb-4">
            <div className="card new-ticket-card">
              <div className="card-header">
                <h5><i className="fa fa-plus-circle"></i> Create New Support Ticket</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmitTicket}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="subject" className="form-label">Subject <span className="required">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="subject"
                        placeholder="Brief description of your issue"
                        value={newTicket.subject}
                        onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="category" className="form-label">Category <span className="required">*</span></label>
                      <select
                        className="form-select"
                        id="category"
                        value={newTicket.category}
                        onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                        required
                      >
                        <option value="" disabled>Select a category</option>
                        <option value="Payment">Payment Issue</option>
                        <option value="Certificate">Certificate Issue</option>
                        <option value="Skill">Change of Skill</option>
                        <option value="ESP Result">ESP Result</option>
                        <option value="Other">Other Issue</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Priority</label>
                    <div className="priority-options">
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="priority"
                          id="priorityLow"
                          value="low"
                          checked={newTicket.priority === 'low'}
                          onChange={() => setNewTicket({...newTicket, priority: 'low'})}
                        />
                        <label className="form-check-label" htmlFor="priorityLow">
                          <span className="priority-indicator priority-low"></span> Low
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="priority"
                          id="priorityMedium"
                          value="medium"
                          checked={newTicket.priority === 'medium'}
                          onChange={() => setNewTicket({...newTicket, priority: 'medium'})}
                        />
                        <label className="form-check-label" htmlFor="priorityMedium">
                          <span className="priority-indicator priority-medium"></span> Medium
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="priority"
                          id="priorityHigh"
                          value="high"
                          checked={newTicket.priority === 'high'}
                          onChange={() => setNewTicket({...newTicket, priority: 'high'})}
                        />
                        <label className="form-check-label" htmlFor="priorityHigh">
                          <span className="priority-indicator priority-high"></span> High
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="message" className="form-label">Message <span className="required">*</span></label>
                    <textarea
                      className="form-control"
                      id="message"
                      rows={5}
                      placeholder="Describe your issue in detail"
                      value={newTicket.message}
                      onChange={(e) => setNewTicket({...newTicket, message: e.target.value})}
                      required
                    ></textarea>
                  </div>
                  
                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => setShowNewTicketForm(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary" 
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <i className="fa fa-spinner fa-spin"></i> Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-paper-plane"></i> Submit Ticket
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        <div className="col-md-4">
          {/* Tickets List */}
          <div className="card tickets-list-card">
            <div className="card-header">
              <div className="filter-controls">
                <button 
                  className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  All
                </button>
                <button 
                  className={`filter-btn ${filter === 'open' ? 'active' : ''}`}
                  onClick={() => setFilter('open')}
                >
                  Open
                </button>
                <button 
                  className={`filter-btn ${filter === 'closed' ? 'active' : ''}`}
                  onClick={() => setFilter('closed')}
                >
                  Closed
                </button>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="loading-state">
                  <i className="fa fa-spinner fa-spin"></i>
                  <p>Loading tickets...</p>
                </div>
              ) : tickets.length > 0 ? (
                <div className="tickets-list">
                  {tickets.map(ticket => (
                    <div 
                      key={ticket.id} 
                      className={`ticket-item ${activeTicket?.id === ticket.id ? 'active' : ''}`}
                      onClick={() => getTicketDetails(ticket.id)}
                    >
                      <div className="ticket-icon">
                        <i className={`fa ${getCategoryIcon(ticket.category)}`}></i>
                      </div>
                      <div className="ticket-content">
                        <div className="ticket-header">
                          <h6 className="ticket-title">{ticket.subject}</h6>
                          <span className={`ticket-status ${getStatusClass(ticket.status)}`}>
                            {ticket.status}
                          </span>
                        </div>
                        <div className="ticket-meta">
                          <span className="ticket-id">{ticket.id}</span>
                          <span className="ticket-date">{formatDate(ticket.updated_at)}</span>
                        </div>
                        <div className="ticket-footer">
                          <span className={`ticket-priority ${getPriorityClass(ticket.priority)}`}>
                            {ticket.priority}
                          </span>
                          <span className="ticket-messages">
                            <i className="fa fa-comments"></i> {ticket.messages.length}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <i className="fa fa-ticket"></i>
                  <p>No tickets found</p>
                  <button 
                    className="btn btn-outline-primary" 
                    onClick={() => setShowNewTicketForm(true)}
                  >
                    Create your first ticket
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-8">
          {/* Ticket Details */}
          {activeTicket ? (
            <div className="card ticket-details-card">
              <div className="card-header">
                <div className="ticket-details-header">
                  <h5 className="ticket-subject">{activeTicket.subject}</h5>
                  <div className="ticket-meta">
                    <span className={`ticket-priority ${getPriorityClass(activeTicket.priority)}`}>
                      {activeTicket.priority}
                    </span>
                    <span className={`ticket-status ${getStatusClass(activeTicket.status)}`}>
                      {activeTicket.status}
                    </span>
                    {activeTicket.status === 'open' && (
                      <button 
                        className="btn btn-sm btn-outline-danger ms-2" 
                        onClick={() => closeTicket(activeTicket.id)}
                      >
                        <i className="fa fa-times-circle"></i> Close Ticket
                      </button>
                    )}
                  </div>
                </div>
                <div className="ticket-details-meta">
                  <span className="ticket-category">
                    <i className={`fa ${getCategoryIcon(activeTicket.category)}`}></i>
                    {activeTicket.category}
                  </span>
                  <span className="ticket-created">
                    <i className="fa fa-calendar"></i>
                    Created: {formatDate(activeTicket.created_at)}
                  </span>
                </div>
              </div>
              <div className="card-body">


                <div className="message-thread">
                  {activeTicket.messages.map((message: any) => (
                    <div 
                      key={message.id} 
                      className={`message-item ${message.sender === 'student' ? 'student-message' : 'admin-message'}`}
                    >
                      <div className="message-avatar">
                        {message.sender === 'student' ? (
                          <i className="fa fa-user"></i>
                        ) : (
                          <i className="fa fa-headset"></i>
                        )}
                      </div>
                      <div className="message-content">
                        <div className="message-header">
                          <span className="message-sender">{message.sender_name}</span>
                          <span className="message-time">{formatDate(message.timestamp)}</span>
                        </div>
                        <div className="message-body">
                          {message.message}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {activeTicket.status === 'open' && (
                  <div className="reply-form">
                    <form onSubmit={handleSubmitReply}>
                      <div className="mb-3">
                        <label htmlFor="replyContent" className="form-label">Your Reply</label>
                        <textarea
                          className="form-control"
                          id="replyContent"
                          rows={3}
                          placeholder="Type your reply here..."
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          required
                        ></textarea>
                      </div>
                      <div className="form-actions">
                        <button 
                          type="submit" 
                          className="btn btn-primary" 
                          disabled={submitting}
                        >
                          {submitting ? (
                            <>
                              <i className="fa fa-spinner fa-spin"></i> Sending...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-paper-plane"></i> Send Reply
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card ticket-details-card empty-details">
              <div className="card-body">
                <div className="empty-state">
                  <i className="fa fa-comments"></i>
                  <p>Select a ticket to view details</p>
                  {tickets.length === 0 && (
                    <button 
                      className="btn btn-outline-primary" 
                      onClick={() => setShowNewTicketForm(true)}
                    >
                      Create your first ticket
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Complaints
